import React, { useEffect, useState } from "react";
import { useAppSelector } from "../redux/store";
import { encrypt } from "../utils/crypto";
import fetchFAQData from "../services/FaqService";
import { Link } from "react-router-dom";
import LogService from "../services/LogService";
import newNoticeService, {
  logViewAgencyInfo,
} from "../services/NewNoticeService";

// Define Types
interface Question {
  question: string;
  answer: string;
}

interface Topic {
  name: string;
  questions: Question[];
}

// Sample topics data
const topicss: Topic[] = [
  {
    name: "General",
    questions: [
      {
        question: "What is your service?",
        answer: "We provide a variety of services including XYZ and ABC.",
      },
      {
        question: "How can I contact support?",
        answer: "Email us at support@example.com or call our helpline.",
      },
      {
        question: "Where is your company located?",
        answer: "Our headquarters are located in New York City.",
      },
      {
        question: "What are your business hours?",
        answer: "We operate from 9 AM to 6 PM, Monday through Friday.",
      },
      {
        question: "Do you offer discounts?",
        answer: "Yes, we offer seasonal discounts and promotional offers.",
      },
    ],
  },
  {
    name: "Billing",
    questions: [
      {
        question: "What payment methods are accepted?",
        answer: "We accept credit cards, PayPal, bank transfers, and more.",
      },
      {
        question: "Can I get a refund?",
        answer: "Refunds are available within 30 days of purchase.",
      },
      {
        question: "Do you offer payment plans?",
        answer:
          "Yes, we offer flexible payment plans. Contact support for details.",
      },
      {
        question: "How do I update my billing information?",
        answer: "You can update billing info in your account settings.",
      },
      {
        question: "Can I download an invoice?",
        answer:
          "Invoices are available in the 'Billing' section of your account.",
      },
    ],
  },
  {
    name: "Technical Support",
    questions: [
      {
        question: "How can I reset my password?",
        answer: "Use the 'Forgot Password' link on the login page.",
      },
      {
        question: "What should I do if I encounter an error?",
        answer: "Contact support and provide a screenshot of the error.",
      },
      {
        question: "How can I check system status?",
        answer: "Visit our status page for live updates.",
      },
      {
        question: "Do you provide support for mobile devices?",
        answer: "Yes, our support covers both desktop and mobile devices.",
      },
      {
        question: "Can I integrate your service with other platforms?",
        answer: "Yes, we offer APIs for integration with third-party services.",
      },
    ],
  },
  {
    name: "Privacy & Security",
    questions: [
      {
        question: "How do you protect my personal information?",
        answer: "We use encryption and secure servers to protect data.",
      },
      {
        question: "Can I delete my account?",
        answer: "Yes, you can request account deletion by contacting support.",
      },
      {
        question: "What data do you collect?",
        answer: "We collect minimal data to improve your experience.",
      },
      {
        question: "How can I update my privacy settings?",
        answer: "Go to your account settings to adjust privacy preferences.",
      },
      {
        question: "Do you share my data with third parties?",
        answer: "No, we do not share data with third parties without consent.",
      },
    ],
  },
  {
    name: "Product Usage",
    questions: [
      {
        question: "Can I use your product offline?",
        answer:
          "Some features are available offline; check your product version.",
      },
      {
        question: "How do I navigate through the product?",
        answer: "Our user manual provides step-by-step navigation guidance.",
      },
      {
        question: "Can I customize the interface?",
        answer:
          "Yes, we offer several themes and layout customization options.",
      },
      {
        question: "Is there a mobile app?",
        answer: "Yes, our mobile app is available on iOS and Android.",
      },
      {
        question: "How can I provide feedback?",
        answer:
          "Submit feedback through the app or email feedback@example.com.",
      },
    ],
  },
  {
    name: "Account Management",
    questions: [
      {
        question: "How can I change my account email?",
        answer: "Go to your account settings and update your email address.",
      },
      {
        question: "What should I do if my account is locked?",
        answer: "Contact support to regain access to your account.",
      },
      {
        question: "Can I have multiple accounts?",
        answer: "Yes, but each account requires a unique email address.",
      },
      {
        question: "How do I cancel my subscription?",
        answer:
          "Go to 'Subscription' settings and select 'Cancel Subscription'.",
      },
      {
        question: "How do I upgrade my account?",
        answer: "Visit the pricing page and choose your upgrade plan.",
      },
    ],
  },
];

const Faq = () => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openTopics, setOpenTopics] = useState<{ [key: string]: boolean }>({});
  const [openQuestions, setOpenQuestions] = useState<{
    [key: string]: boolean;
  }>({});
  const booking = useAppSelector((state) => state.booking);

  const toggleTopic = (topicName: string) => {
    setOpenTopics((prev) => ({ ...prev, [topicName]: !prev[topicName] }));
  };

  const toggleQuestion = (topicName: string, questionIndex: number) => {
    setOpenQuestions((prev) => ({
      ...prev,
      [`${topicName}-${questionIndex}`]: !prev[`${topicName}-${questionIndex}`],
    }));
  };

  // Function to highlight search term
  const highlightText = (text: string, term: string): string => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, "gi");
    const parts = text.split(regex);

    let returnString = "";

    parts.map((part, index) =>
      part.toLowerCase() === term.toLowerCase()
        ? (returnString += `<span key={index} className="bg-yellow-300 font-semibold">
          ${part}
        </span>`)
        : (returnString += `${part}`)
    );

    return returnString;
  };

  useEffect(() => {
    const fetchData = async () => {
      const fetchedTopics = await fetchFAQData();
      if (fetchedTopics) setTopics(fetchedTopics);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 1 && topics && topics.length > 0) {
      const newOpenTopics: { [key: string]: boolean } = {};
      const newOpenQuestions: { [key: string]: boolean } = {};

      topics.forEach((topic) => {
        let topicHasMatch = topic.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

        topic.questions.forEach((q, idx) => {
          const questionHasMatch = q.question
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
          const answerHasMatch = q.answer
            .toLowerCase()
            .includes(searchTerm.toLowerCase());

          if (questionHasMatch || answerHasMatch) {
            newOpenQuestions[`${topic.name}-${idx}`] = true;
            topicHasMatch = true; // Ensure topic is opened if any question/answer matches
          }
        });

        if (topicHasMatch) {
          newOpenTopics[topic.name] = true;
        }
      });

      setOpenTopics(newOpenTopics);
      setOpenQuestions(newOpenQuestions);
    } else {
      setOpenTopics({});
      setOpenQuestions({});
    }
  }, [searchTerm, topics]);

  // Filter topics and questions based on the search input
  const filteredTopics = topics
    .map((topic) => ({
      ...topic,
      questions: topic.questions.filter(
        (q) =>
          q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.answer.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    }))
    .filter(
      (topic) =>
        topic.questions.length > 0 ||
        topic.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const onButtonClickHandler = async () => {
    const requestBody = {
      bookingNumber: booking.bookingNumber,
      name: booking.bookingUsername,
      email: "",
      message: "",
      taskType: "",
    };

    await logViewAgencyInfo(requestBody);
  };

  return (
    <div className="mx-auto p-4 bg-white h-[80vh] overflow-auto 2xl:w-[60vw]">
      <h1 className="text-2xl font-bold mb-4">FAQ – Häufig gestellte Fragen</h1>

      {/* Search Input */}
      <input
        type="text"
        placeholder="Bitte geben Sie ihr Stichwort ein"
        className="w-full p-2 mb-4 border rounded-md"
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {/* Topics and Questions */}
      {filteredTopics.map((topic) => (
        <div key={topic.name} className="border-b py-4">
          {/* Topic Header */}
          <h2
            className="text-xl font-semibold cursor-pointer flex justify-between items-center"
            onClick={() => toggleTopic(topic.name)}
          >
            <span>{topic.name}</span>
            <span>{openTopics[topic.name] ? "-" : "+"}</span>
          </h2>

          {/* Topic Questions */}
          {openTopics[topic.name] && (
            <div className="mt-2 ml-4">
              {topic.questions.map((q, idx) => (
                <div key={idx} className="border-t py-2">
                  {/* Question Header */}
                  <h3
                    className="font-medium cursor-pointer flex justify-between items-center"
                    onClick={() => toggleQuestion(topic.name, idx)}
                  >
                    <span>{q.question}</span>
                    <span>
                      {openQuestions[`${topic.name}-${idx}`] ? "-" : "+"}
                    </span>
                  </h3>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: highlightText(q.answer, searchTerm),
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Chat Button */}

      <Link to={booking.canContact ? "/chat" : "/agency-info"}>
        <button className="btnRounded w-100" onClick={onButtonClickHandler}>
          Kontaktformular
        </button>
      </Link>
    </div>
  );
};

export default Faq;
